/* eslint-disable prefer-destructuring, no-console */
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { v4 } from 'uuid';
import { CORRELATION_ID_HEADER } from '../constants/correlation';
import { SUBMISSION_LINK_TOKEN_HEADER } from '../service/submissionLinkToken';
import { attachEtagInterceptors, ETAG_CACHE_HIT } from './etagCache';

const MAX_RETRIES = 4;

const SENSITIVE_HEADER_NAMES = new Set(
  ['authorization', 'cookie', 'set-cookie', 'x-api-key', 'proxy-authorization', SUBMISSION_LINK_TOKEN_HEADER].map(
    (name) => name.toLowerCase(),
  ),
);

const REDACTED = '[redacted]';

function headerValueToString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  if (value !== undefined && value !== null && typeof value === 'object') return '[object]';
  return String(value);
}

function redactSensitiveHeaders(headers: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    if (value === undefined || value === null) {
      // skip
    } else {
      const key = name.toLowerCase();
      out[name] = SENSITIVE_HEADER_NAMES.has(key) ? REDACTED : headerValueToString(value);
    }
  }
  return out;
}

function headersToPlainObject(headers: unknown): Record<string, string> {
  if (headers && typeof headers === 'object' && !Array.isArray(headers)) {
    const record = headers as Record<string, unknown>;
    return redactSensitiveHeaders(record);
  }
  return {};
}

function getFullUrl(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL || '';
  const url = config.url || '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const separator = base.endsWith('/') ? '' : '/';
  return `${base}${separator}${url}`;
}

// Reuse the client-generated submission identifier as the correlation id for
// submission/response calls (so the whole flow shares one id); otherwise mint one.
function resolveCorrelationId(data: unknown): string {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const body = data as Record<string, unknown>;
    if (typeof body.submissionUuid === 'string' && body.submissionUuid) return body.submissionUuid;
    if (typeof body.uuid === 'string' && body.uuid) return body.uuid;
  }
  return v4();
}

interface AxiosLogConfig {
  axiosLogStart?: number;
  axiosLogRequestHeaders?: Record<string, string>;
  axiosCorrelationId?: string;
}

const isServer = typeof window === 'undefined';

function isFailedRequest(status: number | string): boolean {
  if (status === 'no response') return true;
  if (typeof status === 'number' && status >= 400) return true;
  return false;
}

function getLogLevel(status: number | string): 'info' | 'warn' | 'error' {
  if (status === 'no response') return 'error';
  if (typeof status === 'number' && status >= 500) return 'error';
  if (typeof status === 'number' && status >= 400) return 'warn';
  return 'info';
}

interface AxiosInstanceOptions {
  /**
   * Whether to attach the correlation-id header. A browser-side call to a service whose CORS
   * allow-list does not include that header would fail its preflight, so such clients opt out.
   */
  sendCorrelationId?: boolean;
}

export const createAxiosInstance = (baseURL: string, options: AxiosInstanceOptions = {}): AxiosInstance => {
  const { sendCorrelationId = true } = options;
  const extraHeaders = {} as Record<string, string>;
  const logLevel = isServer ? process.env.LOG_LEVEL : process.env.NEXT_PUBLIC_LOG_LEVEL;
  const isDebug = logLevel === 'debug';
  // Only log every request when level is explicitly info or debug; otherwise log only failures (error/warn/undefined).
  const logOnlyErrors = logLevel !== 'info' && logLevel !== 'debug';

  const instance = axios.create({
    baseURL,
  });

  // Server-side: attach ETag cache interceptors BEFORE axiosRetry so cache intercepts first.
  // On 304: returns cached body. On 200 with ETag: stores compressed body.
  if (isServer) {
    attachEtagInterceptors(instance);
  }

  axiosRetry(instance, {
    retries: MAX_RETRIES,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error: AxiosError) => {
      if (error.response?.status === 304) return false;
      return axiosRetry.isNetworkOrIdempotentRequestError(error);
    },
  });

  instance.interceptors.request.use((request: InternalAxiosRequestConfig & AxiosLogConfig) => {
    for (const [header, value] of Object.entries(extraHeaders)) {
      request.headers[header] = value;
    }
    // Reuse the id across axios-retry attempts so all retries of one request share it.
    const correlationId = request.axiosCorrelationId ?? resolveCorrelationId(request.data);
    if (sendCorrelationId) {
      request.headers[CORRELATION_ID_HEADER] = correlationId;
    }
    request.axiosCorrelationId = correlationId;
    request.axiosLogStart = Date.now();
    if (isDebug && request.headers) {
      const raw =
        typeof request.headers.toJSON === 'function'
          ? (request.headers.toJSON() as Record<string, unknown>)
          : (request.headers as Record<string, unknown>);
      request.axiosLogRequestHeaders = headersToPlainObject(raw);
    }
    return request;
  });

  const logPayload = (
    config: InternalAxiosRequestConfig & AxiosLogConfig,
    status: number | string,
    responseHeaders?: unknown,
    cached?: boolean,
  ) => {
    if (logOnlyErrors && !isFailedRequest(status)) return;
    const start = config.axiosLogStart ?? Date.now();
    const durationMs = Date.now() - start;
    const method = (config.method || 'get').toUpperCase();
    const url = getFullUrl(config);
    const payload: Record<string, unknown> = {
      msg: `Axios ${method} ${url} ${status}`,
      level: getLogLevel(status),
      'http.method': method,
      'http.url': url,
      duration_ms: durationMs,
    };
    if (config.axiosCorrelationId) {
      payload.correlationId = config.axiosCorrelationId;
    }
    if (typeof status === 'number') {
      payload['http.status_code'] = status;
    }
    if (cached !== undefined) {
      payload.cached = cached;
    }
    if (isDebug) {
      payload.requestHeaders = config.axiosLogRequestHeaders ?? {};
      payload.responseHeaders =
        responseHeaders != null ? headersToPlainObject(responseHeaders as Record<string, unknown>) : {};
    }
    console.info(payload);
  };

  instance.interceptors.response.use(
    (response) => {
      const config = response.config as InternalAxiosRequestConfig & AxiosLogConfig;
      const isCached = isServer && config[ETAG_CACHE_HIT] ? true : undefined;
      logPayload(config, isCached ? 304 : response.status, response.headers, isCached);
      return response;
    },
    (error: unknown) => {
      const axiosError = error as AxiosError;
      const config = axiosError.config as (InternalAxiosRequestConfig & AxiosLogConfig) | undefined;
      if (config) {
        const status: number | string = axiosError.response?.status ?? 'no response';
        const responseHeaders = axiosError.response?.headers;
        logPayload(config, status, responseHeaders);
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  );

  return instance;
};
